import { ReactQueryV5 } from "@mindlercare/mindlerapi-client";
import { countryIds } from "@mindlercare/schema";
const { useQuery } = ReactQueryV5;
import { ColumnDef } from "@tanstack/react-table";
import { Booking } from "api/types";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMindlerApi } from "../../../../api/apiv2";
import CONSTANTS from "../../../../core/constants";
import { format, fromUnixTime } from "date-fns";
import useMarketStrategy from "../../../../strategies/market/useMarketStrategy";
import { getCountryCode } from "../../../../helpers/getCountryCode";
import { SlotBookingStatus } from "./SlotBookingStatus";
import { SlotTemplate } from "./SlotTemplate";
import { FALLBACK_VALUE } from "components/Table";
import { logger } from "core/logger";

const useErrorLog = (error: unknown) => {
  useEffect(() => {
    if (error) {
      const e = error as {
        data?: { code?: string; httpStatus?: number } | null;
        message?: string;
      };
      logger.error("Failed fetch all psychologists", {
        error: {
          code: e.data?.code,
          status: e.data?.httpStatus,
          name: e.message,
        },
      });
    }
  }, [error]);
};

export const useBookingColumns = (bookings: Booking[]) => {
  const { showExtraInfoPatientBookings } = useMarketStrategy();
  const { t } = useTranslation();

  const api = useMindlerApi();
  const {
    data: psychologists,
    isSuccess,
    error,
  } = useQuery(
    // Use the currently selected market, not the JWT market claim: admins are
    // allowed to switch market without re-authenticating (see RequiresAuth), so
    // the JWT can hold the market they logged in on rather than the one they
    // are looking at. includeInactive so bookings with psychologists who have
    // since been deactivated still resolve to a name.
    api.psychologists.findAll.queryOptions({
      countryId: countryIds[getCountryCode()],
      includeInactive: true,
    }),
  );

  useErrorLog(error);

  const data = useMemo(() => {
    if (!bookings.length) {
      return [
        {
          date: FALLBACK_VALUE,
          time: FALLBACK_VALUE,
          method: {
            templateId: -1,
            method: FALLBACK_VALUE,
          },
          psychologist: FALLBACK_VALUE,
          paid: FALLBACK_VALUE,
          id: FALLBACK_VALUE,
          slotId: FALLBACK_VALUE,
          ...(showExtraInfoPatientBookings
            ? { bookedAt: FALLBACK_VALUE, paymentMethod: FALLBACK_VALUE }
            : {}),
        },
      ];
    }

    return bookings
      .map((booking) => {
        const { slotId, psychologistId } = booking;
        const date = format(
          fromUnixTime(booking.startsAt),
          CONSTANTS.DATE_FORMAT.DEFAULT_DATE_FORMAT,
        );
        const startTime = format(
          fromUnixTime(booking.startsAt),
          CONSTANTS.DEFAULT_TIME_FORMAT,
        );
        const endTime = format(
          fromUnixTime(booking.endsAt),
          CONSTANTS.DEFAULT_TIME_FORMAT,
        );
        const time = `${startTime} ${t("TO")} ${endTime}`;

        const assignedPsychologist = isSuccess
          ? psychologists.find((x) => x.psychologistId === psychologistId)
          : null;

        const bookedAt = booking.bookedAt
          ? format(
              new Date(booking.bookedAt),
              CONSTANTS.DATE_FORMAT.YEAR_NAME_DATE_FORMAT,
            )
          : FALLBACK_VALUE;

        const psychologist = assignedPsychologist
          ? `${
              assignedPsychologist.user.firstName
            } ${assignedPsychologist.user.lastName}`
          : FALLBACK_VALUE;

        return {
          date,
          time,
          id: `${booking.slotId}`,
          method: { templateId: booking.templateId, method: booking.method },
          psychologist,
          paid: { paid: booking.payed, status: booking.status },
          slotId,
          ...(showExtraInfoPatientBookings
            ? {
                paymentMethod: booking.paymentType || FALLBACK_VALUE,
                bookedAt,
              }
            : {}),
        };
      })
      .sort((a, b) => {
        if (a.date < b.date) return 1;
        if (a.date > b.date) return -1;
        if (a.time < b.time) return 1;
        if (a.time > b.time) return -1;
        return 0;
      });
  }, [bookings, psychologists, showExtraInfoPatientBookings, t, isSuccess]);

  const columns = useMemo<ColumnDef<(typeof data)[number]>[]>(
    () => [
      {
        header: "DATE",
        accessorKey: "date",
      },
      {
        header: "TIME",
        accessorKey: "time",
      },
      {
        header: "BOOKING_TYPE",
        accessorKey: "method",
        cell: ({ getValue }) => {
          const { templateId, method } = getValue() as {
            templateId: number;
            method: Booking["status"];
          };

          return <SlotTemplate templateId={templateId} method={method} />;
        },
      },
      {
        header: "PSYCHOLOGIST",
        accessorKey: "psychologist",
        meta: {
          sensitive: true,
        },
      },
      {
        header: "STATUS",
        accessorKey: "paid",
        cell: ({ getValue }) => {
          const { paid, status } = getValue() as {
            paid: boolean;
            status: Booking["status"];
          };
          return <SlotBookingStatus paid={paid} status={status} />;
        },
      },
      {
        header: "BOOKING_ID",
        accessorKey: "slotId",
      },
      ...(showExtraInfoPatientBookings
        ? [
            {
              accessorKey: "paymentMethod",
              header: "PAYMENT_METHOD",
            },
            {
              accessorKey: "bookedAt",
              header: "BOOKED",
            },
          ]
        : []),
    ],
    [showExtraInfoPatientBookings],
  );

  return {
    columns,
    data,
  };
};
